import { Alert, Button, Card, CardContent, CardHeader, Collapse, Grid, InputAdornment, Link, Switch, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import ConfigurationOption from "components/ConfigurationOption";
import SAMLMetadataDialog from "components/SAMLMetadataDialog";
import Loading from "components/Loading";
import { useLogin } from "providers/Login";
import { LoadingButton } from "@mui/lab";
import { useNotification } from "providers/Notification";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { formatDate } from "helpers/dateFormat";
import { ListInput } from "components/ListInput";

// NS-753
//AdminSamlRbac is the "adminSamlRbac" block of /api/config: it maps SAML group
//values to dashboard roles.
interface AdminSamlRbac {
    enable: boolean
    groupAttribute: string
    roleGroups: {
        full_admin: string[]
        policy_admin: string[]
    }
}

//toAdminSamlRbac reads the block from the server. The block, or a key of
//roleGroups, can be missing or null.
function toAdminSamlRbac(v: any): AdminSamlRbac {
    return {
        enable: v?.enable || false,
        groupAttribute: v?.groupAttribute || "",
        roleGroups: {
            full_admin: v?.roleGroups?.full_admin || [],
            policy_admin: v?.roleGroups?.policy_admin || [],
        },
    };
}

function cleanGroupValues(values: string[]): string[] {
    return values.map(x => x.trim()).filter(x => x !== "");
}

//cleanAdminSamlRbac trims the attribute and the group values, and removes
//empty values, before the block is validated and sent.
function cleanAdminSamlRbac(v: AdminSamlRbac): AdminSamlRbac {
    return {
        enable: v.enable,
        groupAttribute: v.groupAttribute.trim(),
        roleGroups: {
            full_admin: cleanGroupValues(v.roleGroups.full_admin),
            policy_admin: cleanGroupValues(v.roleGroups.policy_admin),
        },
    };
}

interface LicenseInfo {
    name: string
    creationDate: Date
    expirationDate: Date
    expired: boolean
}

function Configuration() {
    const notification = useNotification();
    const login = useLogin();
    const [finishedLoading, setFinishedLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [reqModLogEnabled, setReqModLogEnabled] = useState(false);
    const [enableSAML, setEnableSAML] = useState(false);

    const [SAMLMetadataUrl, setSAMLMetadataUrl] = useState("");
    const [SAMLMetadata, setSAMLMetadata] = useState("");

    const [requireIcapAuth, setRequireIcapAuth] = useState(false);
    const [UserSAMLMetadataUrl, setUserSAMLMetadataUrl] = useState("");
    const [UserSAMLMetadata, setUserSAMLMetadata] = useState("");
    const [adminSamlUsernameAttribute, setAdminSamlUsernameAttribute] = useState("username");
    const [icapSamlUsernameAttribute, setIcapSamlUsernameAttribute] = useState("username");
    const [adminSamlRbac, setAdminSamlRbac] = useState<AdminSamlRbac>(toAdminSamlRbac(undefined));

    const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | undefined>(undefined);

    const [samlDialogOpen, setSamlDialogOpen] = useState(false);
    const [userSamlDialogOpen, setUserSamlDialogOpen] = useState(false);

    const [enableActivitylogClear, setEnableActivitylogClear] = useState(false);
    const [activityMaxAge, setActivityMaxAge] = useState<number>();
    const [schedule, setActivitySchedule] = useState("");

    const [enableActivity, setEnableActivity] = useState(false);
    const [logBypassedTraffic, setLogBypassedTraffic] = useState(false);

    const [samlAvailable, setSamlAvailable] = useState(false);
    const [samlNotAvailableReason, setSamlNotAvailableReason] = useState("");

    const [originalSettings, setOriginalSettings] = useState<any>();

    const req = login.GetAxios();

    const loadData = async () => {
        setFinishedLoading(false);
        if (!req) {
            return; //not logged in, nothing to do
        }
        try {
            const respConfig = await req.get("/api/config");
            const respLicense = await req.get("/api/config/license");
            const respSamlAvailability = await req.get("/api/config/samlavailability");
            
            const settings = respConfig.data;
            const license = respLicense.data;
            const samlAvailability = respSamlAvailability.data;


            setSamlAvailable(samlAvailability?.dashboard?.available || false);
            setSamlNotAvailableReason(samlAvailability?.dashboard?.reason || "");


            setLicenseInfo({
                name: license.username,
                creationDate: new Date(license.createdDate),
                expirationDate: new Date(license.expireDate),
                expired: license.expired,
            });

            setOriginalSettings(settings);

            setEnabled(settings.enabled);
            setReqModLogEnabled(settings.reqModLogEnable);

            setEnableSAML(settings.saml.enable);
            setSAMLMetadata(settings.saml.metadata);

            setActivitySchedule(settings.activity.cleanupSchedule);
            setActivityMaxAge(Math.round(settings.activity.maxAge / 86400));
            setEnableActivitylogClear((settings.activity.maxAge && settings.activity.cleanupSchedule) ? true : false);
            setEnableActivity(settings.activity.enable);
            setLogBypassedTraffic(settings.activity.logBypassed || false);
            setUserSAMLMetadata(settings.userSAMLMetadata || "");
            setAdminSamlUsernameAttribute(settings.adminSamlUsernameAttribute || "username");
            setIcapSamlUsernameAttribute(settings.icapSamlUsernameAttribute || "username");
            setAdminSamlRbac(toAdminSamlRbac(settings.adminSamlRbac));
            setRequireIcapAuth(settings.requireLogin || false);
            setFinishedLoading(true);
        } catch (err) {
            notification.Display({
                title: "Error",
                message: "Error fetching configuration data",
                type: "error",
            });
        }
    };

    useEffect(() => {
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const SaveConfiguration = async () => {
        if (!req) {
            return; //not logged in, nothing to do
        }

        if (enableActivitylogClear) {
            if (!activityMaxAge) {
                notification.Display({
                    title: "Error",
                    message: "Max age is required",
                    type: "error",
                });
                return;
            }
            if (!schedule) {
                notification.Display({
                    title: "Error",
                    message: "Schedule is required",
                    type: "error",
                });
                return;
            }
        }

        // NS-753
        const samlRbac = cleanAdminSamlRbac(adminSamlRbac);
        if (samlRbac.enable && !samlRbac.groupAttribute) {
            notification.Display({
                title: "Error",
                message: "Group Attribute is required when role-based access is enabled",
                type: "error",
            });
            return;
        }

        setSaving(true);

        try {
            await req.put("/api/config", {
                ...originalSettings,
                enabled,
                reqModLogEnable: reqModLogEnabled,
                saml: {
                    enable: enableSAML,
                    metadataUrl: SAMLMetadataUrl,
                    metadata: SAMLMetadata,
                },
                activity: {
                    enable: enableActivity,
                    logBypassed: logBypassedTraffic,
                    maxAge: enableActivitylogClear ? ((activityMaxAge || 0) * 86400) : undefined,
                    cleanupSchedule: enableActivitylogClear ? schedule : undefined,
                },
                userSAMLMetadata: UserSAMLMetadata,
                userSAMLMetadataURL: UserSAMLMetadataUrl,
                adminSamlUsernameAttribute,
                adminSamlRbac: samlRbac,
                icapSamlUsernameAttribute,
                requireLogin: requireIcapAuth,
            });
            notification.Display({
                type: "success",
                title: "Success",
                message: "Configuration saved successfully",
            });
        } catch (err) {
            notification.Display({
                title: "Error",
                message: "Error saving configuration",
                type: "error",
            });
        }

        setSaving(false);
        void loadData();
    };


    return <>
        <Loading finished={finishedLoading}>
            <Typography variant="h1">Configuration</Typography>

            <Box display="flex" flexDirection="column">

                <Card sx={{ marginTop: "30px", padding: "0px" }}>
                    <CardHeader component={Typography} titleTypographyProps={{ variant: "h3" }} title={"Dashboard Security"} />
                    <CardContent sx={{ padding: "10px" }}>
                        <Grid container>
                            <Grid item sm={12} >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row"
                                }}>
                                    <ConfigurationOption
                                        id="enable-saml-check"
                                        label="Enable SAML"
                                        description="Enable login using SAML"
                                        checked={enableSAML} onChange={setEnableSAML}
                                        disabled={!samlAvailable}
                                    />
                                    <Button
                                        sx={{ marginLeft: "10px" }}
                                        id="saml-open-dialog"
                                        onClick={() => {
                                            setSamlDialogOpen(true);
                                        }}
                                        variant="outlined"
                                        disabled={!samlAvailable}
                                    >
                                        Set IDp Metadata
                                    </Button>
                                    {samlAvailable && <Link sx={{ marginLeft: "10px" }} href="/saml/metadata" download>Download SP metadata</Link>}
                                </Box>
                                {samlNotAvailableReason !== "" ?  <Typography variant="caption" color="text.secondary">{samlNotAvailableReason}</Typography> : undefined}
                                {enableSAML && samlAvailable && <Box sx={{ marginTop: "15px" }}>
                                    <TextField
                                        size="small"
                                        label="Username Attribute"
                                        helperText="The SAML attribute to use as the admin username. Use 'NameID' to use the SAML Subject."
                                        value={adminSamlUsernameAttribute}
                                        onChange={(e) => setAdminSamlUsernameAttribute(e.target.value)}
                                        placeholder="username"
                                        sx={{ minWidth: "300px" }}
                                    />
                                    <Box sx={{ marginTop: "15px" }}>
                                        <SamlRbacOptions value={adminSamlRbac} onChange={setAdminSamlRbac} />
                                    </Box>
                                </Box>}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ marginTop: "30px", padding: "0px" }}>
                    <CardHeader component={Typography} titleTypographyProps={{ variant: "h3" }} title={"Activity Configuration"} />
                    <CardContent sx={{ padding: "10px" }}>
                        <Grid container>
                            <Grid item sm={12} >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row"
                                }}>
                                    <ConfigurationOption
                                        id="enable-activity"
                                        label="Enable Activity Log"
                                        description="Enable writing activity log with request data"
                                        checked={enableActivity} onChange={setEnableActivity}
                                    />
                                </Box>
                            </Grid>
                            <Grid item sm={12} >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row"
                                }}>
                                    <ConfigurationOption
                                        id="log-bypassed-traffic"
                                        label="Log Bypassed Traffic"
                                        description="Record traffic that matches the Scanning Mode bypass list in Activity and Syslog"
                                        checked={logBypassedTraffic} onChange={setLogBypassedTraffic}
                                        disabled={!enableActivity}
                                    />
                                </Box>
                            </Grid>
                            <Grid item sm={12} >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row"
                                }}>
                                    <ConfigurationOption
                                        id="enable-reqmod-log"
                                        label="REQMOD Scanning"
                                        description="Enable scanning of request data from REQMOD"
                                        checked={reqModLogEnabled} onChange={setReqModLogEnabled}
                                    />
                                </Box>
                            </Grid>
                            <Grid item sm={12} >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row"
                                }}>
                                    <ConfigurationOption
                                        id="enable-clear-old-activity-log"
                                        label="Periodically clean activity log"
                                        description="Periodically delete old activity log entries following a specified schedule"
                                        checked={enableActivitylogClear} onChange={setEnableActivitylogClear}
                                    />
                                    <TextField
                                        id="clear-old-activity-log-age"
                                        sx={{ marginLeft: "10px" }}
                                        type="number"
                                        label="Max Age"
                                        variant="outlined"
                                        disabled={!enableActivitylogClear}
                                        InputProps={{ endAdornment: <InputAdornment position="end">Days</InputAdornment> }}
                                        helperText="Entries entries older than specified will be deleted"
                                        value={activityMaxAge}
                                        onChange={(x) => setActivityMaxAge(Number(x.target.value) || undefined)}
                                    />
                                    <TextField
                                        id="clear-old-activity-log-schedule"
                                        sx={{ marginLeft: "10px" }}
                                        label="Schedule"
                                        variant="outlined"
                                        helperText="The delete operation will follow the schedule defined by a crontab specification"
                                        placeholder="* * * *"
                                        disabled={!enableActivitylogClear}
                                        value={schedule}
                                        onChange={(x) => setActivitySchedule(x.target.value)}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ marginTop: "30px", padding: "0px" }}>
                    {
                        licenseInfo?.expired ? <>
                            <CardHeader component={Typography} titleTypographyProps={{ variant: "h3" }} title={"License Information"}
                                subheaderTypographyProps={{ variant: "body1", color: "error" }} subheader={"Your license is expired, please contact us at support@nullafi.com"} />
                            <CardContent sx={{ padding: "10px 25px" }}>
                                <Grid container sx={{
                                    marginBottom: "10px",
                                    marginTop: "20px"
                                }}>
                                    <Grid item sm={3}>
                                        <Typography variant="h4">Licensed To</Typography>
                                        <Typography color="text.secondary">{licenseInfo?.name}</Typography>
                                    </Grid>
                                    <Grid item sm={1} />
                                    <Grid item sm={3}>
                                        <Typography variant="h4">Expiring at</Typography>
                                        <Typography color="error">{licenseInfo && formatDate(licenseInfo.expirationDate)}</Typography>
                                    </Grid>
                                    <Grid item sm={1} />
                                    <Grid item sm={3}>
                                        <Typography variant="h4">Last updated</Typography>
                                        <Typography color="text.secondary">{licenseInfo && formatDate(licenseInfo.creationDate)}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </> : <>
                            <CardHeader component={Typography} titleTypographyProps={{ variant: "h3" }} title={"License Information"}
                                subheaderTypographyProps={{ variant: "body2", color: "text.secondary" }} subheader={"Please make sure to contact us at support@nullafi.com when your license is close to expiration "} />
                            <CardContent sx={{ padding: "10px 25px" }}>
                                <Grid container sx={{
                                    marginBottom: "10px",
                                    marginTop: "20px"
                                }}>
                                    <Grid item sm={3}>
                                        <Typography variant="h4">Licensed To</Typography>
                                        <Typography color="text.secondary">{licenseInfo?.name}</Typography>
                                    </Grid>
                                    <Grid item sm={1} />
                                    <Grid item sm={3}>
                                        <Typography variant="h4">Expiring at</Typography>
                                        <Typography color="text.secondary">{licenseInfo && formatDate(licenseInfo.expirationDate)}</Typography>
                                    </Grid>
                                    <Grid item sm={1} />
                                    <Grid item sm={3}>
                                        <Typography variant="h4">Last updated</Typography>
                                        <Typography color="text.secondary">{licenseInfo && formatDate(licenseInfo.creationDate)}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </>                  
                    }
                   
                </Card>

            </Box>

            <Box sx={{ marginTop: "10px" }}>
                <ShieldEnabledComponent
                    enabled={enabled}
                    onChange={setEnabled}
                />
            </Box>

            <Box display="flex" sx={{ marginTop: "50px", marginBottom: "50px" }}>
                <LoadingButton
                    id="save-changes-button"
                    variant="contained"
                    sx={{ marginLeft: "auto" }}
                    onClick={SaveConfiguration}
                    loading={saving}>Save Changes</LoadingButton>
            </Box>

            <SAMLMetadataDialog
                open={samlDialogOpen}
                onClose={() => {
                    setSamlDialogOpen(false);
                }}
                value={(SAMLMetadata && { type: "xml", metadata: SAMLMetadata }) || (SAMLMetadataUrl && { type: "url", url: SAMLMetadataUrl }) || undefined}
                onSave={(v) => {
                    setSamlDialogOpen(false);
                    if (v.type === "url") {
                        setSAMLMetadata("");
                        setSAMLMetadataUrl(v.url);
                    } else if (v.type === "xml") {
                        setSAMLMetadata(v.metadata);
                        setSAMLMetadataUrl("");
                    }
                }}
            />

            <SAMLMetadataDialog
                open={userSamlDialogOpen}
                onClose={() => {
                    setUserSamlDialogOpen(false);
                }}
                value={(UserSAMLMetadata && { type: "xml", metadata: UserSAMLMetadata }) || (UserSAMLMetadataUrl && { type: "url", url: UserSAMLMetadataUrl }) || undefined}
                onSave={(v) => {
                    setUserSamlDialogOpen(false);
                    if (v.type === "url") {
                        setUserSAMLMetadata("");
                        setUserSAMLMetadataUrl(v.url);
                    } else if (v.type === "xml") {
                        setUserSAMLMetadata(v.metadata);
                        setUserSAMLMetadataUrl("");
                    }
                }}
            />
        </Loading>
    </>;
}

interface SamlRbacOptionsProps {
    value: AdminSamlRbac
    onChange: (v: AdminSamlRbac) => void
}

// NS-753
//SamlRbacOptions edits the mapping from SAML groups to dashboard roles.
function SamlRbacOptions(props: SamlRbacOptionsProps) {
    const v = props.value;

    const setGroups = (role: keyof AdminSamlRbac["roleGroups"], groups: string[]) => {
        props.onChange({ ...v, roleGroups: { ...v.roleGroups, [role]: groups } });
    };

    return <ConfigurationOption
        id="enable-saml-rbac-check"
        label="Enable role-based access"
        description="Assign a dashboard role from the SAML group attribute. Users without a mapped group get Read-only."
        checked={v.enable}
        onChange={(enable) => props.onChange({ ...v, enable })}
    >
        {v.enable && <Box sx={{ marginTop: "15px", maxWidth: "600px" }}>
            <TextField
                id="saml-group-attribute-input"
                size="small"
                label="Group Attribute"
                required
                fullWidth
                value={v.groupAttribute}
                onChange={(e) => props.onChange({ ...v, groupAttribute: e.target.value })}
            />
            <GroupAttributeHelp />

            {/* group values can contain commas (LDAP DNs), so only ENTER adds a value */}
            <ListInput
                id="saml-full-admin-groups-input"
                size="small"
                label="Full Admin groups"
                placeholder="Type a group value and press ENTER"
                delimiters={[]}
                value={v.roleGroups.full_admin}
                onChange={(groups) => setGroups("full_admin", groups)}
                onDelete={(group) => setGroups("full_admin", v.roleGroups.full_admin.filter(x => x !== group))}
            />
            <ListInput
                id="saml-policy-admin-groups-input"
                size="small"
                label="Policy Admin groups"
                placeholder="Type a group value and press ENTER"
                delimiters={[]}
                value={v.roleGroups.policy_admin}
                onChange={(groups) => setGroups("policy_admin", groups)}
                onDelete={(group) => setGroups("policy_admin", v.roleGroups.policy_admin.filter(x => x !== group))}
            />
            <Typography variant="caption" color="text.secondary" component="div">
                All other SAML users get Read-only.
            </Typography>

            <Alert severity="warning" sx={{ marginTop: "10px" }}>
                Keep at least one local account for recovery. An incorrect mapping gives Read-only to every SAML user.
            </Alert>
            {cleanGroupValues(v.roleGroups.full_admin).length === 0 && <Alert severity="warning" sx={{ marginTop: "10px" }}>
                No group gives Full Admin. Only local accounts can change the configuration.
            </Alert>}
        </Box>}
    </ConfigurationOption>;
}

//GroupAttributeHelp lists the usual group attribute of each identity provider.
//The list is collapsed by default so that the form stays short.
function GroupAttributeHelp() {
    const [open, setOpen] = useState(false);

    const providers: { name: string, attribute: React.ReactNode }[] = [
        { name: "Microsoft Entra ID", attribute: <><code>http://schemas.microsoft.com/ws/2008/06/identity/claims/groups</code> (map the group Object IDs)</> },
        { name: "AD FS", attribute: <code>http://schemas.xmlsoap.org/claims/Group</code> },
        { name: "Okta", attribute: <code>groups</code> },
        { name: "Google Workspace", attribute: <>the name set in "App attribute", usually <code>groups</code></> },
    ];

    return <Box sx={{ marginTop: "5px", marginLeft: "14px" }}>
        <Typography variant="caption" color="text.secondary" component="div">
            The SAML attribute that contains the groups of the user. If the IdP sends a FriendlyName, use the FriendlyName.
            {" "}
            <Link
                id="saml-group-attribute-help-toggle"
                component="button"
                type="button"
                variant="caption"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
                {open ? "Hide common values" : "Show common values"}
            </Link>
        </Typography>
        <Collapse in={open}>
            <Box component="ul" sx={{ margin: "5px 0 0 0", paddingLeft: "20px", wordBreak: "break-all" }}>
                {providers.map(x => <Typography key={x.name} component="li" variant="caption" color="text.secondary">
                    {x.name}: {x.attribute}
                </Typography>)}
            </Box>
        </Collapse>
    </Box>;
}

interface ShieldEnabledComponentProps {
    enabled: boolean
    onChange?: (v: boolean) => void
}

function ShieldEnabledComponent(props: ShieldEnabledComponentProps) {
    return <Box sx={props.enabled ? {
        background: "rgba(226, 242, 255, 0.5)",
        border: (theme) => `1px solid ${theme.palette.custom.shieldEnabled}`,
        boxSizing: "border-box",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05), 0px 4px 8px rgba(58, 117, 232, 0.05)",
        borderRadius: "10px",
        padding: "10px",
        alignItems: "center",
        display: "flex"
    } : {
        background: "rgba(255, 232, 244, 0.25)",
        border: (theme) => `1px solid ${theme.palette.custom.shieldDisabled}`,
        boxSizing: "border-box",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05), 0px 4px 8px rgba(58, 117, 232, 0.05)",
        borderRadius: "10px",
        padding: "10px",
        alignItems: "center",
        display: "flex"
    }}>
        {
            props.enabled ? <>
                <CheckCircleOutlineOutlinedIcon sx={{ marginRight: "5px", fill: (theme) => theme.palette.primary.main }} />
                <Typography variant="h4">Nullafi Shield is enabled</Typography>
                <InfoOutlinedIcon sx={{ marginRight: "5px", marginLeft: "auto" }} fontSize="small" />
                <span>You can use this option to temporally disable Nullafi Shield</span>
            </> : <>
                <ErrorOutlineOutlinedIcon sx={{ marginRight: "5px", fill: (theme) => theme.palette.custom.shieldDisabledIcon }} />
                <Typography variant="h4">Nullafi Shield is disabled</Typography>
                <InfoOutlinedIcon sx={{ marginRight: "5px", marginLeft: "auto" }} fontSize="small" />
                <span>You can use this option to enable Nullafi Shield</span>
            </>
        }

        <Switch checked={props.enabled} onChange={() => props.onChange && props.onChange(!props.enabled)} />
    </Box>;
}


export default Configuration;